import { TableProps } from "antd/es/table";
import { PromotionItem, PromoStatus } from "../../../api/admin/webshop/Promotion";
import { Button, Tag, Space, Typography, Tooltip } from "antd";
import {
  LockOutlined,
  GlobalOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { formatSgtDate } from "./promoDateUtils";

const { Text } = Typography;

interface PromoTableColumnsProps {
  onEdit: (promoCode: string) => void;
}

const STATUS_TAG_COLORS: Record<PromoStatus | string, string> = {
  ACTIVE: "green",
  SCHEDULED: "blue",
  DRAFT: "default",
  PAUSED: "orange",
  EXPIRED: "red",
};

export const getPromoTableColumns = ({
  onEdit,
}: PromoTableColumnsProps): TableProps<PromotionItem>["columns"] => [
  {
    title: "Discount Code",
    dataIndex: "promoCode",
    key: "promoCode",
    render: (promoCode: string, record: PromotionItem) => {
      const isPrivate = record.type === "PROMO#PRIVATE";
      return (
        <Space direction="vertical" size={2}>
          <Space align="center" size={6}>
            <Text strong style={{ fontFamily: "monospace", fontSize: 13 }}>
              {promoCode}
            </Text>
          </Space>
        </Space>
      );
    },
  },
  {
    title: "Campaign / Title",
    dataIndex: "name",
    key: "name",
    render: (name: string, record: PromotionItem) => (
      <Space direction="vertical" size={2}>
        <Text strong>{record.customerTitle || name || record.promoCode}</Text>
        {/* {record.name && record.name !== record.customerTitle && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Internal: {record.name}
          </Text>
        )} */}
      </Space>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status: PromoStatus) => {
      const normalizedStatus = status || "DRAFT";
      const color = STATUS_TAG_COLORS[normalizedStatus] || "default";
      return <Tag color={color}>{normalizedStatus}</Tag>;
    },
  },
  {
    title: "Discount",
    dataIndex: "discount",
    key: "discount",
    render: (discount: PromotionItem["discount"]) => {
      if (!discount) return "—";
      const currency = discount.currency || "SGD";
      if (discount.type?.toUpperCase() === "PERCENTAGE") {
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ color: "rgb(255, 70, 37)" }}>
              {discount.value}% off
            </Text>
            {discount.maxCap != null && discount.maxCap > 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Cap: ${discount.maxCap} {currency}
              </Text>
            )}
          </Space>
        );
      }
      return (
        <Text strong style={{ color: "rgb(255, 70, 37)" }}>
          ${discount.value} {currency} off
        </Text>
      );
    },
  },
  {
    title: "Subtotal Req.",
    key: "minOrderValue",
    render: (_: any, record: PromotionItem) => {
      const minVal = record.eligibility?.minOrderValue ?? 0;
      const currency = record.discount?.currency || "SGD";
      return minVal > 0 ? `$${minVal} ${currency}` : "None ($0)";
    },
  },
  {
    title: "Redemptions (Used / Limit)",
    key: "redemptions",
    render: (_: any, record: PromotionItem) => {
      const used = record.globalUsedCount ?? 0;
      const limit =
        record.globalLimit != null ? record.globalLimit : "∞ Unlimited";
      return (
        <Text>
          <Text strong>{used}</Text> / {limit}
        </Text>
      );
    },
  },
  {
    title: "Start Date (SGT)",
    dataIndex: "startAt",
    key: "startAt",
    render: (startAt: string | undefined) => formatSgtDate(startAt),
  },
  {
    title: "End Date (SGT)",
    dataIndex: "endAt",
    key: "endAt",
    render: (endAt: string | undefined, record: PromotionItem) => {
      if (record.noExpiry || !endAt) {
        return <Tag color="blue">No Expiry</Tag>;
      }
      return formatSgtDate(endAt);
    },
  },
  {
    title: "Action",
    key: "action",
    render: (_: any, record: PromotionItem) => (
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => onEdit(record.promoCode)}
      >
        Edit / Details
      </Button>
    ),
  },
];
